import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
// Common Components
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import PerPageBlock from '../../common/PerPageBlock';
// API & Utils
import { getOpeningBalances, createOpeningBalance, fetchOpeningBalance, destroyOpeningBalance, updateOpeningBalance } from '../../../api/OpeningBalancesAPI';
import { getProducts } from '../../../api/ProductsAPI';
import { getUoms } from '../../../api/UomsAPI';
import { getFranchiseeSettings } from '../../../api/FranchiseeSettingsAPI';
import { handleApiError } from '../../../api/risapi';
// Hooks & Helpers
import { useNavigate, useLocation } from 'react-router-dom';
// Date Picker & Date Formatting
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';
import Swal from 'sweetalert2';
import { Modal, Button } from 'react-bootstrap';

const OpeningBalancesManagerPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    // --- State for the List ---
    const [entityName, setEntityName] = useState('Opening Balance');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);
    const [obList, setObList] = useState({ paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null });

    // --- State for the "Add New" Form ---
    const [newOb, setNewOb] = useState({
        product_id: null, uom_id: null, quantity: '', uom_cost_price: '',
        entry_date: new Date(), conversion_factor: 1,
    });
    const [newObErrors, setNewObErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // --- State for Edit Modal ---
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentEditOb, setCurrentEditOb] = useState(null);
    const [editObErrors, setEditObErrors] = useState({});
    const [loadingEditData, setLoadingEditData] = useState(false);
    const [filteredEditUomOptions, setFilteredEditUomOptions] = useState([]);

    // --- General State ---
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);
    const [saveCompleted, setSaveCompleted] = useState(false);

    // Dropdown Options
    const [productOptions, setProductOptions] = useState([]);
    const [uomOptions, setUomOptions] = useState([]);
    const [filteredUomOptions, setFilteredUomOptions] = useState([]);

    // Fixed OB Date Setting
    const [fixedObDate, setFixedObDate] = useState(null);
    const [isObDateFixed, setIsObDateFixed] = useState(false);

    // --- Fetch Dropdown Data & Settings ---
    useEffect(() => {
        let isMounted = true;
        setLoadingDropdowns(true);
        const fetchInitialData = async () => {
            try {
                const [productResponse, uomResponse, settingsResponse] = await Promise.all([
                    new Promise((resolve) => getProducts({ paginate: false, with: ['productUnits.uom', 'baseUom'] }, resolve)), // ** FIX: Eager load units **
                    new Promise((resolve) => getUoms({ paginate: false }, resolve)),
                    new Promise((resolve) => getFranchiseeSettings(resolve)),
                ]);
                if (!isMounted) return;

                if (productResponse.products && Array.isArray(productResponse.products)) {
                    setProductOptions(productResponse.products.map(p => ({ value: p.id, label: `${p.name} ${p.code ? `(${p.code})` : ''}`, productData: p })));
                } else { throw new Error('Invalid product data'); }
                if (uomResponse.uoms && Array.isArray(uomResponse.uoms)) {
                     const options = uomResponse.uoms.map(u => ({ value: u.id, label: `${u.name} (${u.short_name})` }));
                     setUomOptions(options); setFilteredUomOptions(options);
                 } else { throw new Error('Invalid UOM data'); }

                if (settingsResponse.settings?.opening_balance_date) {
                     const obDate = settingsResponse.settings.opening_balance_date;
                     setFixedObDate(obDate); setIsObDateFixed(true);
                     try { const parsed = parseISO(obDate + 'T00:00:00'); if (!isNaN(parsed)) setNewOb(prev => ({...prev, entry_date: parsed })); } catch (e) { console.error("Error parsing fixed OB date");}
                } else { setIsObDateFixed(false); setNewOb(prev => ({...prev, entry_date: new Date() })); }

            } catch (error) {
                 if (!isMounted) return;
                 console.error("Error fetching initial data:", error);
                 setToaster({ type: 'danger', heading: 'Error', message: 'Could not load required data. Please refresh.' }); setShowToaster(true);
                 setProductOptions([]); setUomOptions([]); setFilteredUomOptions([]);
            } finally {
                 if (isMounted) setLoadingDropdowns(false);
            }
        };
        fetchInitialData();
        return () => { isMounted = false; };
    }, []);

    // --- Fetch OB List Data ---
    useEffect(() => {
        document.title = 'Manage Opening Balances';
        let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };
        setViewDataLoading(true);

        getOpeningBalances(filters, function (response) {
            setViewDataLoading(false);
            if (response.opening_balances) {
                let data = []; let currentP = 1; let lastP = 1; let responsePP = perPage;
                if (Array.isArray(response.opening_balances)) { data = response.opening_balances; }
                else if (response.opening_balances.data && Array.isArray(response.opening_balances.data)) { data = response.opening_balances.data; currentP = response.opening_balances.current_page; lastP = response.opening_balances.last_page; responsePP = response.opening_balances.per_page; }
                else { console.error("API structure error:", response.opening_balances); handleApiError(response); return; }
                setObList({ paginate: filters.paginate, per_page: responsePP, data: data, current_page: currentP, tot_page: lastP });
            } else {
                 handleApiError(response); setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Opening Balances.' }); setShowToaster(true);
                 setObList(prev => ({ ...prev, data: [], current_page: 1, tot_page: 1 }));
            }
        });
    }, [currentPage, searchTermCompleted, saveCompleted, perPage]);

    // --- Success Message Handling ---
    useEffect(() => {
         if (successMessage) { setShowToaster(true); setToaster({ type: 'success', heading: 'Success', message: successMessage }); navigate(location.pathname, { replace: true, state: {} }); }
     }, [successMessage, location.pathname, navigate]);


    // --- Filter UOMs Helper ---
    // Renamed to be generic so it can be used by both Add and Edit forms
    const getFilteredUoms = useCallback((productId) => {
        if (!productId || !Array.isArray(productOptions) || productOptions.length === 0 || !Array.isArray(uomOptions) || uomOptions.length === 0) {
            return uomOptions || [];
        }
        const selectedProduct = productOptions.find(p => p.value === productId);
        if (selectedProduct) {
            const productData = selectedProduct.productData; 
            const validUomIds = new Set();
            if (productData?.base_uom_id) validUomIds.add(productData.base_uom_id);
            if (Array.isArray(productData?.product_units)) { 
                productData.product_units.forEach(unit => { if (unit.uom_id) validUomIds.add(unit.uom_id); }); 
            }
            return uomOptions.filter(uom => validUomIds.has(uom.value));
        } else { 
            return uomOptions; 
        }
    }, [productOptions, uomOptions]);

    // --- Input Handlers for "Add New" Form ---
    const handleNewProductChange = (selectedOption) => {
         const productId = selectedOption ? selectedOption.value : null;
         setNewOb(prev => ({ ...prev, product_id: productId, uom_id: null, conversion_factor: 1 }));
         setNewObErrors(prev => ({ ...prev, product_id: null, uom_id: null }));
         // Update the filtered list for the Add New form
         setFilteredUomOptions(getFilteredUoms(productId));
     };
    const handleNewUomChange = (selectedOption) => {
         const uomId = selectedOption ? selectedOption.value : null; let factor = 1;
         const productData = productOptions.find(p => p.value === newOb.product_id)?.productData;
         if (productData && uomId && productData.base_uom_id !== uomId) {
             factor = productData.product_units?.find(pu => pu.uom_id === uomId)?.conversion_factor || 1;
         }
         setNewOb(prev => ({ ...prev, uom_id: uomId, conversion_factor: factor }));
         setNewObErrors(prev => ({ ...prev, uom_id: null, conversion_factor: null }));
     };
    const handleNewInputChange = (e) => {
         const { name, value } = e.target;
         if ((name === 'quantity' || name === 'uom_cost_price') && value !== '' && !/^\d*\.?\d*$/.test(value)) return;
         setNewOb(prev => ({ ...prev, [name]: value }));
         setNewObErrors(prev => ({ ...prev, [name]: null }));
     };
    const handleNewDateChange = (date) => { if (!isObDateFixed) setNewOb(prev => ({ ...prev, entry_date: date })); };

    // --- Save Handler for "Add New" Form ---
    const handleAddNew = () => {
        setNewObErrors({}); setSaving(true);
        let formattedDate = null;
        if (newOb.entry_date instanceof Date && !isNaN(newOb.entry_date)) { formattedDate = format(newOb.entry_date, 'yyyy-MM-dd'); }
        else { setNewObErrors({ entry_date: 'Invalid date.' }); setSaving(false); return; }

        const dataToSend = {
             product_id: newOb.product_id, uom_id: newOb.uom_id, quantity: newOb.quantity,
             uom_cost_price: newOb.uom_cost_price, entry_date: formattedDate, conversion_factor: newOb.conversion_factor,
        };

        createOpeningBalance(dataToSend, (response) => {
            setSaving(false);
            if (response.opening_balance) {
                 setToaster({ type: 'success', heading: 'Success', message: 'Opening Balance saved successfully.' }); setShowToaster(true);
                 setSaveCompleted(prev => !prev);
                 setNewOb({
                     product_id: null, uom_id: null, quantity: '', uom_cost_price: '',
                     entry_date: isObDateFixed && fixedObDate ? parseISO(fixedObDate + 'T00:00:00') : new Date(),
                     conversion_factor: 1
                 });
                 setFilteredUomOptions(uomOptions);
            } else {
                 handleApiError(response, setNewObErrors);
                 setToaster({ type: 'danger', heading: 'Error', message: response.error?.response?.data?.message || `Could not save Opening Balance.` }); setShowToaster(true);
            }
        });
    };

   // --- Edit/Delete Handlers for List ---
    const handleEditClick = (id) => {
        setLoadingEditData(true);
        setEditObErrors({});
        setShowEditModal(true);
        fetchOpeningBalance(id, (response) => {
            setLoadingEditData(false);
            if (response.opening_balance) {
                const fetched = response.opening_balance;
                let entryDate = new Date();
                const dateStringToParse = (isObDateFixed && fixedObDate) ? fixedObDate : fetched.entry_date;
                if (dateStringToParse) { try { entryDate = parseISO(dateStringToParse + 'T00:00:00'); if(isNaN(entryDate)) entryDate = new Date(); } catch(e){ entryDate = new Date(); } }
                
                setCurrentEditOb({
                    id: fetched.id,
                    product_id: fetched.product_id,
                    uom_id: fetched.uom_id,
                    quantity: fetched.quantity || '',
                    uom_cost_price: fetched.uom_cost_price || '',
                    entry_date: entryDate,
                    conversion_factor: fetched.conversion_factor || 1,
                    product_name: fetched.product?.name || 'Unknown Product'
                });
                
                // ** FIX: Use the helper to set the edit modal's UOM options immediately **
                setFilteredEditUomOptions(getFilteredUoms(fetched.product_id));

            } else {
                 handleApiError(response, setEditObErrors);
                 setToaster({ type: 'danger', heading: 'Error', message: 'Could not load Opening Balance data.' });
                 setShowToaster(true);
                 setShowEditModal(false);
            }
        });
    };

    // --- Handlers for Edit Modal ---
    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setCurrentEditOb(null);
        setEditObErrors({});
        setFilteredEditUomOptions([]); // Clear modal specific options
    };

    const handleEditInputChange = (e) => {
         const { name, value } = e.target;
         if ((name === 'quantity' || name === 'uom_cost_price') && value !== '' && !/^\d*\.?\d*$/.test(value)) return;
         setCurrentEditOb(prev => ({ ...prev, [name]: value }));
         setEditObErrors(prev => ({ ...prev, [name]: null }));
    };
    const handleEditUomChange = (selectedOption) => {
         const uomId = selectedOption ? selectedOption.value : null; let factor = 1;
         const productData = productOptions.find(p => p.value === currentEditOb.product_id)?.productData;
         if (productData && uomId && productData.base_uom_id !== uomId) {
             factor = productData.product_units?.find(pu => pu.uom_id === uomId)?.conversion_factor || 1;
         }
         setCurrentEditOb(prev => ({ ...prev, uom_id: uomId, conversion_factor: factor }));
         setEditObErrors(prev => ({ ...prev, uom_id: null, conversion_factor: null }));
    };
    const handleEditDateChange = (date) => { if (!isObDateFixed) setCurrentEditOb(prev => ({ ...prev, entry_date: date })); };

    const handleUpdateSave = () => {
         setEditObErrors({}); setSaving(true);
         let formattedDate = null;
         if (currentEditOb.entry_date instanceof Date && !isNaN(currentEditOb.entry_date)) { formattedDate = format(currentEditOb.entry_date, 'yyyy-MM-dd'); }
         else { setEditObErrors({ entry_date: 'Invalid date.' }); setSaving(false); return; }

         const dataToSend = {
             id: currentEditOb.id, product_id: currentEditOb.product_id, uom_id: currentEditOb.uom_id,
             quantity: currentEditOb.quantity, uom_cost_price: currentEditOb.uom_cost_price,
             entry_date: formattedDate, conversion_factor: currentEditOb.conversion_factor,
         };

         updateOpeningBalance(dataToSend, (response) => {
             setSaving(false);
             if (response.opening_balance) {
                  setToaster({ type: 'success', heading: 'Success', message: 'Opening Balance updated successfully.' }); setShowToaster(true);
                  setSaveCompleted(prev => !prev);
                  handleCloseEditModal();
             } else {
                  handleApiError(response, setEditObErrors);
                  setToaster({ type: 'danger', heading: 'Error', message: response.error?.response?.data?.message || `Could not update Opening Balance.` }); setShowToaster(true);
             }
         });
    };

    const handleDeleteClick = (id) => {
        fetchOpeningBalance(id, function (result) {
            if (result.opening_balance) {
                const deleteItem = result.opening_balance;
                Swal.fire({
                    title: 'Are you sure?',
                    html: `
                        <p>Delete Opening Balance for:</p>
                        <ul class="delete-details-list">
                            <li><strong>ID:</strong> ${deleteItem.id}</li>
                            <li><strong>Product:</strong> ${deleteItem.product?.name || 'N/A'}</li>
                            <li><strong>Date:</strong> ${deleteItem.entry_date}</li>
                            <li><strong>Qty:</strong> ${deleteItem.quantity} ${deleteItem.uom?.short_name || ''}</li>
                        </ul>
                        <p class="text-danger">WARNING: This action may detach used stock movements and trigger reallocation. Ensure this is intended.</p>
                        <p>This action cannot be undone.</p>`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: 'Yes, delete it!'
                }).then((dialogResult) => {
                    if (dialogResult.isConfirmed) {
                        destroyOpeningBalance(deleteItem, function (apiResponse) {
                            if (apiResponse.opening_balance) {
                                Swal.fire('Deleted!', 'Record deleted. Stock reallocation (if needed) will be processed.', 'success');
                                setSaveCompleted(prev => !prev);
                            } else {
                                Swal.fire('Error!', apiResponse.error?.response?.data?.message || 'Could not delete Opening Balance.', 'error');
                            }
                        });
                    }
                });
            } else { Swal.fire('Error!', 'Could not fetch details for deletion.', 'error'); }
        });
    };

    // Helper to format date
    const formatDate = (dateString) => {
         if (!dateString) return 'N/A';
         try { return format(parseISO(dateString), 'dd-MMM-yyyy'); } catch (error) { return dateString; }
     };
    const isLoading = viewDataLoading || loadingDropdowns;

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Manage Opening Balances'>
                        <li><a className="f-s-14 f-w-500" href="#">Inventory</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Opening Balances</a></li>
                    </AppBreadCrumb>

                    {/* --- Add New Section --- */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <h5 className="mb-0">Add New Opening Balance Entry</h5>
                                     {isObDateFixed && <small className='text-muted d-block'>Entry date is fixed to: {formatDate(fixedObDate)}</small>}
                                </div>
                                <div className="card-body">
                                    {loadingDropdowns ? (
                                        <div className="text-center p-3">Loading setup data...</div>
                                    ) : (
                                        <form className="app-form needs-validation" noValidate onSubmit={(e) => {e.preventDefault(); handleAddNew();}}>
                                            <div className="row align-items-end">
                                                {/* Entry Date */}
                                                <div className="col-lg col-md-4 mb-2">
                                                    <label className="form-label">Entry Date *</label>
                                                    <DatePicker selected={newOb.entry_date} onChange={handleNewDateChange} dateFormat="yyyy-MM-dd" className="form-control form-control-sm" wrapperClassName="d-block" disabled={isObDateFixed} readOnly={isObDateFixed}/>
                                                    <span className="text-danger small">{newObErrors?.entry_date}</span>
                                                </div>
                                                {/* Product */}
                                                <div className="col-lg col-md-4 mb-2">
                                                    <label className="form-label">Product *</label>
                                                    <Select
                                                        options={productOptions}
                                                        value={Array.isArray(productOptions) ? productOptions.find(o => o.value === newOb.product_id) : null}
                                                        onChange={handleNewProductChange} placeholder="Select Product" isClearable isDisabled={loadingDropdowns}
                                                        styles={{ control: base => ({...base, minHeight: '31px', height: '31px'}), valueContainer: base => ({...base, height: '31px', padding: '0 6px'}), indicatorsContainer: base => ({...base, height: '31px'}) }}
                                                    />
                                                    <span className="text-danger small">{newObErrors?.product_id}</span>
                                                </div>
                                                {/* UOM */}
                                                <div className="col-lg col-md-4 mb-2">
                                                    <label className="form-label">Unit *</label>
                                                    <Select
                                                        options={filteredUomOptions}
                                                        value={Array.isArray(filteredUomOptions) ? filteredUomOptions.find(o => o.value === newOb.uom_id) : null}
                                                        onChange={handleNewUomChange} placeholder="Select UOM" isClearable isDisabled={!newOb.product_id || loadingDropdowns}
                                                        styles={{ control: base => ({...base, minHeight: '31px', height: '31px'}), valueContainer: base => ({...base, height: '31px', padding: '0 6px'}), indicatorsContainer: base => ({...base, height: '31px'}) }}
                                                    />
                                                    <span className="text-danger small">{newObErrors?.uom_id}</span>
                                                </div>
                                                {/* Quantity */}
                                                <div className="col-lg col-md-4 mb-2">
                                                    <label className="form-label">Quantity *</label>
                                                    <input type="number" step="0.01" min="0" className="form-control form-control-sm text-end" placeholder="0.00" name="quantity" value={newOb.quantity || ''} onChange={handleNewInputChange} required />
                                                    <span className="text-danger small">{newObErrors?.quantity}</span>
                                                </div>
                                                {/* Cost Price */}
                                                <div className="col-lg col-md-4 mb-2">
                                                    <label className="form-label">Cost (per UOM) *</label>
                                                    <input type="number" step="0.01" min="0" className="form-control form-control-sm text-end" placeholder="0.00" name="uom_cost_price" value={newOb.uom_cost_price || ''} onChange={handleNewInputChange} required />
                                                    <span className="text-danger small">{newObErrors?.uom_cost_price}</span>
                                                </div>
                                                <input type="hidden" name="conversion_factor" value={newOb.conversion_factor || 1} />
                                                <div className="col-lg-auto col-md-4 mb-2">
                                                    <label className="form-label d-md-block d-none">&nbsp;</label>
                                                    <button className="btn btn-red btn-sm w-100" type="submit" disabled={saving || isLoading}>
                                                        {saving ? 'Adding...' : '+ Add Entry'}
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                     )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* --- List Section --- */}
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                 <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
                                     <h5 className="mb-0">Existing Opening Balances</h5>
                                     <div className='mt-2 mt-md-0'>
                                        <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                     </div>
                                 </div>
                                <div className="card-body p-0">
                                    {/* ... (List Table and Paginator - Unchanged) ... */}
                                    <div className="d-flex flex-wrap m-3 border-bottom pb-3 align-items-center">
                                        {obList.data.length > 0 ? (
                                            <>
                                                <AppPaginator entityName={entityName} currentPage={obList.current_page} totalPages={obList.tot_page} setCurrentPage={setCurrentPage} />
                                                <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                            </>
                                        ) : ( !viewDataLoading && <p className="text-muted w-100 mb-0">No Opening Balances found.</p> )}
                                        {(viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-hover table-striped mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Entry Date</th>
                                                    <th>Product</th>
                                                    <th className='text-end'>Quantity</th>
                                                    <th>UOM</th>
                                                    <th className='text-end'>Cost (per UOM)</th>
                                                    <th className='text-end'>Base Qty</th>
                                                    <th className='text-end'>Base Cost</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {!viewDataLoading && obList.data.map((obEntry) => (
                                                    <tr key={obEntry.id}>
                                                        <td>{formatDate(obEntry.entry_date)}</td>
                                                        <td>{obEntry.product?.name || 'N/A'} {obEntry.product?.code ? `(${obEntry.product.code})` : ''}</td>
                                                        <td className='text-end'>{parseFloat(obEntry.quantity || 0).toFixed(2)}</td>
                                                        <td>{obEntry.uom?.short_name || 'N/A'}</td>
                                                        <td className='text-end'>{parseFloat(obEntry.uom_cost_price || 0).toFixed(2)}</td>
                                                        <td className='text-end text-muted'>{parseFloat(obEntry.base_quantity_received || 0).toFixed(2)}</td>
                                                        <td className='text-end text-muted'>{parseFloat(obEntry.base_landed_cost || 0).toFixed(2)}</td>
                                                        <td>
                                                            <button className="btn btn-sm btn-outline-success me-1 px-1 py-0" title="Edit" onClick={() => handleEditClick(obEntry.id)}> <i className="ti ti-edit f-s-14"></i> </button>
                                                            <button className="btn btn-sm btn-outline-danger px-1 py-0" title="Delete" onClick={() => handleDeleteClick(obEntry.id)}> <i className="ti ti-trash f-s-14"></i> </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {viewDataLoading && ( <tr><td colSpan="8" className="text-center">Loading...</td></tr> )}
                                                {!viewDataLoading && obList.data.length === 0 && ( <tr><td colSpan="8" className="text-center">No Opening Balances Found</td></tr> )}
                                            </tbody>
                                        </table>
                                    </div>

                                    
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* --- Edit Modal --- */}
            <Modal show={showEditModal} onHide={handleCloseEditModal} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Opening Balance</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingEditData ? (
                        <div className="text-center p-5"><div className="loader_14"></div></div>
                    ) : currentEditOb ? (
                        <form className="app-form" onSubmit={(e) => e.preventDefault()}>
                            <div className="row">
                                {/* Entry Date */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Entry Date *</label>
                                    <DatePicker selected={currentEditOb.entry_date} onChange={handleEditDateChange} dateFormat="yyyy-MM-dd" className="form-control" wrapperClassName="d-block" disabled={isObDateFixed} readOnly={isObDateFixed}/>
                                    {isObDateFixed && <small className='text-muted'>Date is fixed for all opening balances.</small>}
                                    <span className="text-danger small">{editObErrors?.entry_date}</span>
                                </div>
                                {/* Product (Read-Only) */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Product *</label>
                                    <input type="text" className="form-control" value={currentEditOb.product_name || `Product ID: ${currentEditOb.product_id}`} readOnly disabled />
                                    <span className="text-danger small">{editObErrors?.product_id}</span>
                                </div>
                                {/* UOM */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Unit *</label>
                                    {/* ** FIX: Use the correct filtered list ** */}
                                    <Select
                                        options={filteredEditUomOptions}
                                        value={Array.isArray(filteredEditUomOptions) ? filteredEditUomOptions.find(o => o.value === currentEditOb.uom_id) : null}
                                        onChange={handleEditUomChange} placeholder="Select UOM" isClearable
                                        isDisabled={!currentEditOb.product_id || loadingDropdowns}
                                        menuPortalTarget={document.body} // Optional: helps with modal z-index issues
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    />
                                    <span className="text-danger small">{editObErrors?.uom_id}</span>
                                </div>
                                {/* Quantity */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Quantity *</label>
                                    <input type="number" step="0.01" min="0" className="form-control text-end" placeholder="0.00" name="quantity" value={currentEditOb.quantity || ''} onChange={handleEditInputChange} required />
                                    <span className="text-danger small">{editObErrors?.quantity}</span>
                                </div>
                                {/* Cost Price */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Cost (per UOM) *</label>
                                    <input type="number" step="0.01" min="0" className="form-control text-end" placeholder="0.00" name="uom_cost_price" value={currentEditOb.uom_cost_price || ''} onChange={handleEditInputChange} required />
                                    <span className="text-danger small">{editObErrors?.uom_cost_price}</span>
                                </div>
                                {/* Conversion Factor (Readonly) */}
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Conversion Factor</label>
                                    <input type="text" className="form-control" value={currentEditOb.conversion_factor || '1.00'} readOnly disabled />
                                    <span className="text-danger small">{editObErrors?.conversion_factor}</span>
                                </div>
                            </div>
                        </form>
                    ) : (
                        <div className="text-center p-3 text-danger">Could not load data.</div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseEditModal} disabled={saving}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateSave} disabled={saving || loadingEditData}>
                        {saving ? 'Updating...' : 'Update Opening Balance'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default OpeningBalancesManagerPage;